'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Check, ExternalLink, Minus, Users } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/layout/dialog';
import { GetContactsButton } from '@/components/features/reports/GetContactsButton';
import { JOB_ACTION_BUTTON_CLASS } from '@/components/features/jobs/job-action-button-styles';
import usePaywallGuard from '@/hooks/usePaywallGuard';
import { useEventTracking } from '@/hooks/useEventTracking';
import { EventType } from '@/api/types/events';
import { buildHiringTrackingContext } from '@/lib/analytics/hiring-funnel';

interface ApplyComparisonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobUrl: string | undefined;
  jobTitle: string;
  companyName: string;
  companyId?: string | number;
  jobLocation?: string;
  jobId?: string | number;
  jobDescription?: string;
  jobContextData?: Record<string, unknown>;
  autoRedirectSeconds?: number;
}

const CONTACT_ADVANTAGES = [
  "Message the hiring team, don't sit and wait.",
  '10× higher reply rate than normal applications',
  'Contacts selected for your background and this role',
  'Draft created for you — review and send in 60 seconds',
];

export function ApplyComparisonDialog({
  open,
  onOpenChange,
  jobUrl,
  jobTitle,
  companyName,
  companyId,
  jobLocation,
  jobId,
  jobDescription,
  jobContextData,
  autoRedirectSeconds,
}: ApplyComparisonDialogProps) {
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const handleApplyNormallyRef = useRef<(useNewTab?: boolean) => void>(() => {});
  const previousOpenRef = useRef(open);
  const hasTrackedStandardCtaVisibleRef = useRef(false);
  const standardCtaVisibilityRef = useRef<HTMLDivElement | null>(null);
  const { trackEvent } = useEventTracking();

  const { handleAction: handleGuardedAction, isDisabled } = usePaywallGuard({
    signInFeatureSlug: 'reports',
  });

  const trackingContext = useMemo(
    () =>
      buildHiringTrackingContext({
        targetType: 'job',
        companyName,
        companyId,
        jobTitle,
        jobId,
        jobLocation,
        jobUrl,
      }),
    [companyId, companyName, jobId, jobLocation, jobTitle, jobUrl]
  );

  useEffect(() => {
    if (open && !previousOpenRef.current) {
      trackEvent({
        event_type: EventType.DIALOG_OPEN,
        element_id: 'apply-comparison-dialog',
        event_details: {
          action: 'apply_comparison_dialog_open',
          funnel: 'application_path_selection',
          ...trackingContext,
        },
      });
    } else if (!open && previousOpenRef.current) {
      trackEvent({
        event_type: EventType.DIALOG_CLOSE,
        element_id: 'apply-comparison-dialog',
        event_details: {
          action: 'apply_comparison_dialog_close',
          funnel: 'application_path_selection',
          ...trackingContext,
        },
      });
    }
    previousOpenRef.current = open;
  }, [open, trackEvent, trackingContext]);

  useEffect(() => {
    if (!open) {
      hasTrackedStandardCtaVisibleRef.current = false;
      return;
    }

    const targetNode = standardCtaVisibilityRef.current;
    if (!targetNode) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting || hasTrackedStandardCtaVisibleRef.current) {
          return;
        }

        hasTrackedStandardCtaVisibleRef.current = true;
        trackEvent({
          event_type: EventType.SCROLL,
          element_id: 'apply-comparison-apply-normally-visibility',
          event_details: {
            action: 'standard_apply_option_visible',
            funnel: 'application_path_selection',
            ...trackingContext,
          },
        });
      },
      { threshold: 0.25 }
    );

    observer.observe(targetNode);

    return () => observer.disconnect();
  }, [open, trackEvent, trackingContext]);

  useEffect(() => {
    if (!open || !autoRedirectSeconds || autoRedirectSeconds <= 0) {
      setCountdown(null);
      return;
    }
    setCountdown(autoRedirectSeconds);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open, autoRedirectSeconds]);

  useEffect(() => {
    if (countdown === 0) {
      handleApplyNormallyRef.current(false);
    }
  }, [countdown]);

  const handleContactHiringManager = () => {
    trackEvent({
      event_type: EventType.CLICK,
      element_id: 'apply-comparison-contact-hiring-manager',
      event_details: {
        action: 'contact_hiring_manager_selected',
        funnel: 'application_path_selection',
        ...trackingContext,
      },
    });
    void handleGuardedAction(() => {
      onOpenChange(false);
      setIsContactDialogOpen(true);
    });
  };

  const handleApplyNormally = (useNewTab = true) => {
    trackEvent({
      event_type: EventType.CLICK,
      element_id: 'apply-comparison-apply-normally',
      event_details: {
        action: 'apply_normally_selected',
        funnel: 'application_path_selection',
        ...trackingContext,
      },
    });
    trackEvent({
      event_type: EventType.JOB_APPLY,
      element_id: 'apply-comparison-apply-normally',
      event_details: {
        action: 'apply_normally_selected',
        funnel: 'application_path_selection',
        ...trackingContext,
      },
    });
    if (jobUrl) {
      trackEvent({
        event_type: EventType.REDIRECT,
        element_id: 'apply-comparison-apply-normally',
        event_details: {
          action: 'apply_normally_redirect_initiated',
          funnel: 'application_path_selection',
          destination: jobUrl,
          ...trackingContext,
        },
      });
      if (useNewTab) {
        window.open(jobUrl, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = jobUrl;
      }
    } else {
      trackEvent({
        event_type: EventType.ERROR,
        element_id: 'apply-comparison-apply-normally',
        event_details: {
          action: 'apply_normally_redirect_missing_url',
          funnel: 'application_path_selection',
          ...trackingContext,
        },
      });
    }
    onOpenChange(false);
  };

  handleApplyNormallyRef.current = handleApplyNormally;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-[480px] w-full rounded-none border border-border border-t border-t-primary p-0 overflow-hidden flex flex-col bg-white"
          style={{ boxShadow: 'var(--shadow-warm-lg)', maxHeight: '90vh' }}
        >
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-5 sm:p-6 space-y-4">
              <DialogHeader className="space-y-1.5 text-left">
                <p className="text-2xs font-semibold uppercase tracking-[0.16em] text-[var(--burgundy-core,#7f1d1d)]">
                  How to apply
                </p>
                <DialogTitle className="text-lg font-bold leading-snug text-foreground">
                  Get in front of the hiring team at {companyName}
                </DialogTitle>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Most candidates wait in the queue. You don&apos;t have to.
                </p>
              </DialogHeader>

              {/* Primary: Direct outreach card */}
              <div className="border border-[var(--burgundy-core,#7f1d1d)] bg-white">
                <div className="bg-[var(--burgundy-core,#7f1d1d)] text-white text-2xs font-semibold uppercase tracking-[0.14em] text-center py-1.5 px-3">
                  Recommended
                </div>
                <div className="p-5 space-y-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[var(--burgundy-core,#7f1d1d)]" />
                    <h3 className="text-sm font-bold text-foreground">Reach the hiring team directly</h3>
                  </div>

                  <ul className="space-y-2.5">
                    {CONTACT_ADVANTAGES.map((text) => (
                      <li key={text} className="flex items-start gap-2">
                        <div className="flex-shrink-0 mt-0.5 h-4 w-4 rounded-full bg-[var(--burgundy-core,#7f1d1d)]/10 flex items-center justify-center">
                          <Check className="h-2.5 w-2.5 text-[var(--burgundy-core,#7f1d1d)]" />
                        </div>
                        <span className="text-xs text-foreground leading-snug">{text}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    data-track-id="apply-comparison-contact-hiring-manager"
                    onClick={handleContactHiringManager}
                    disabled={isDisabled}
                    className={JOB_ACTION_BUTTON_CLASS}
                    style={{
                      background: 'var(--burgundy-core, #7f1d1d)',
                      borderColor: 'var(--burgundy-core, #7f1d1d)',
                      color: '#fff',
                    }}
                  >
                    Find Hiring Contacts
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>

              {/* Divider */}
              <div className="my-2 flex items-center gap-3">
                <div className="flex-1 border-t border-border/60" />
                <span className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground/60">or</span>
                <div className="flex-1 border-t border-border/60" />
              </div>

              {/* Secondary: Standard application */}
              <div ref={standardCtaVisibilityRef} className="border border-border/60 bg-gray-50/50 p-4 space-y-3.5">
                <p className="text-2xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Standard application</p>
                <ul className="space-y-2.5">
                  {[
                    'May redirect through job boards or third-party career portals',
                    'Often requires creating an account and filling out multi-step forms',
                    'No direct contact — your resume enters the general applicant pool alongside hundreds of others',
                  ].map((text) => (
                    <li key={text} className="flex items-start gap-2.5">
                      <div className="flex-shrink-0 mt-0.5 h-4 w-4 rounded-full bg-muted flex items-center justify-center">
                        <Minus className="h-2.5 w-2.5 text-muted-foreground/60" />
                      </div>
                      <span className="text-xs text-muted-foreground leading-snug">{text}</span>
                    </li>
                  ))}
                </ul>
                <div className="relative">
                  <Button
                    data-track-id="apply-comparison-apply-normally"
                    onClick={() => handleApplyNormally()}
                    variant="outline"
                    className={`${JOB_ACTION_BUTTON_CLASS} border-border font-semibold overflow-hidden`}
                  >
                    {countdown !== null && countdown > 0 ? (
                      <>
                        Apply — redirecting in {countdown}…
                        <ExternalLink className="h-3.5 w-3.5" />
                      </>
                    ) : (
                      <>
                        Apply on company site
                        <ExternalLink className="h-3.5 w-3.5" />
                      </>
                    )}
                    {countdown !== null && countdown > 0 && autoRedirectSeconds && (
                      <>
                        <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-[var(--burgundy-core,#7f1d1d)]/20" />
                        <span
                          className="pointer-events-none absolute left-0 bottom-0 h-[2px] bg-[var(--burgundy-core,#7f1d1d)] transition-[width] duration-300 ease-out"
                          style={{ width: `${((autoRedirectSeconds - countdown) / autoRedirectSeconds) * 100}%` }}
                        />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <GetContactsButton
        type="job"
        companyName={companyName}
        companyId={companyId}
        jobTitle={jobTitle}
        jobLocation={jobLocation}
        jobUrl={jobUrl}
        jobId={jobId}
        jobDescription={jobDescription}
        jobContextData={jobContextData}
        hideTriggerButton
        controlledOpen={isContactDialogOpen}
        onControlledOpenChange={setIsContactDialogOpen}
      />
    </>
  );
}
